import { Button } from "@/components/ui/button";
import { Plus, Minus } from "lucide-react";

interface Product {
  _id: string;
  name?: string;
  price: number;
  images?: { url: string; thumbnailUrl?: string }[];
  quantityRules?: { minimum: number; maximum: number };
}

interface OrderQuantitySelectorProps {
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  total?: number;
  tipAmount?: number;
}

export function OrderQuantitySelector({
  products,
  quantities,
  onQuantityChange,
  total,
  tipAmount = 0,
}: OrderQuantitySelectorProps) {
  return (
    <div className="space-y-2 my-4">
      {products.map((product) => {
        const firstImage = product.images?.[0];
        const imageUrl = firstImage?.thumbnailUrl ?? firstImage?.url;
        const min = product.quantityRules?.minimum ?? 1;
        const max = product.quantityRules?.maximum ?? 10;
        const showQuantitySelect = max > 1;
        const currentQty = quantities[product._id] ?? min;

        return (
          <div
            key={product._id}
            className="flex items-center gap-4"
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={product.name || "Product"}
                className="h-14 w-14 object-cover rounded-md shrink-0"
              />
            ) : null}
            <div className="flex-1 min-w-0">
              <div className="font-medium">
                {product.name || `Product ${product._id}`}
              </div>
              <div className="text-sm text-gray-500">
                ${product.price?.toFixed(2) || "0.00"}
              </div>
            </div>
            {showQuantitySelect ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 border rounded-md">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (currentQty > min) {
                        onQuantityChange(product._id, currentQty - 1);
                      }
                    }}
                    disabled={currentQty <= min}
                    className="h-8 w-8"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="min-w-[2rem] text-center font-medium">
                    {currentQty}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (currentQty < max) {
                        onQuantityChange(product._id, currentQty + 1);
                      }
                    }}
                    disabled={currentQty >= max}
                    className="h-8 w-8"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
      <hr className="border-gray-300 my-4" />
      {total !== undefined && (
        <div className="flex justify-end font-semibold text-lg">
          Total: ${(total + tipAmount).toFixed(2)}
        </div>
      )}
    </div>
  );
}
